/*!
 * Bearbottom Fit Finder — UI controller for the PDP "Find my size" modal.
 * Recommendation logic lives in fit-engine.js (window.BBFitEngine); this file only handles
 * the modal, storage, variant selection and analytics. Spec: FIT_FINDER_BUILD_SPEC.md.
 *
 * Data comes from <script type="application/json" data-fit-finder> rendered by
 * snippets/fit-finder.liquid. One instance per picker (a page can have more than one).
 */
(function () {
  'use strict';
  if (window.BBFitFinder) return;

  var E = window.BBFitEngine;
  var STORE_KEY = 'bb_fit_profile_v1';
  var SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  var FIT_CLASSES = ['athletic', 'modern', 'relaxed', 'comfort', 'boxy', 'supportive'];
  var instances = {};
  var modal = null;          // shared modal shell, built on first open
  var active = null;         // instance the modal is currently showing

  /* ---------- helpers ---------- */
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function fill(str, vars) { return String(str || '').replace(/\[(\w+)\]/g, function (m, k) { return vars && vars[k] != null ? vars[k] : m; }); }
  function num(s) { var m = String(s).match(/[\d.]+/); return m ? parseFloat(m[0]) : NaN; }
  function loadProfile() {
    try { var v = JSON.parse(window.localStorage.getItem(STORE_KEY)); return v && v.heightIn ? v : null; } catch (e) { return null; }
  }
  // One profile for tops and bottoms. Shape answers are kept only once given, so a shopper who started on
  // bottoms is asked chest + stomach the first time they open a top (and the reverse for thighs).
  function saveProfile(p) {
    try {
      var out = { heightIn: p.heightIn, weightLb: p.weightLb, waist: p.waist, savedAt: new Date().toISOString() };
      ['chestShape', 'stomachShape', 'thighShape'].forEach(function (k) { if (p[k]) out[k] = p[k]; });
      window.localStorage.setItem(STORE_KEY, JSON.stringify(out));
    } catch (e) { /* storage unavailable: keep working without saving */ }
  }
  function clearProfile() { try { window.localStorage.removeItem(STORE_KEY); } catch (e) {} }
  function track(evt, params) {
    var o = { event: evt };
    for (var k in params) if (params[k] !== undefined) o[k] = params[k];
    (window.dataLayer = window.dataLayer || []).push(o);
  }

  /* ---------- spec prep ---------- */
  // "Athletic fit\n..." -> "Athletic". custom.fit_category wins over the spec sheet's fit.
  function fitFromCategory(txt) {
    var first = String(txt || '').split(/\r?\n/)[0].trim().split(/\s+/)[0];
    var k = (first || '').toLowerCase();
    return FIT_CLASSES.indexOf(k) > -1 ? first.charAt(0).toUpperCase() + k.slice(1) : null;
  }

  // Pass the engine only the sizes the product actually sells.
  function prepSpec(data) {
    var src = data.spec, spec = {}, k;
    for (k in src) spec[k] = src[k];
    spec.fit = fitFromCategory(data.fitCategory) || src.fit;
    if (spec.waist_offset_override === null) delete spec.waist_offset_override;
    var sold = data.productSizes || [];
    if (sold.length) {
      var keep = [];
      (src.sizes || []).forEach(function (s, i) { if (sold.indexOf(s) > -1) keep.push(i); });
      spec.sizes = keep.map(function (i) { return src.sizes[i]; });
      spec.measurements = {};
      for (k in src.measurements) {
        var arr = src.measurements[k];
        spec.measurements[k] = Array.isArray(arr) ? keep.map(function (i) { return arr[i]; }) : arr;
      }
    }
    // Inseams: keep the lengths this product sells and remember the option value to click.
    // Match on the label ("5.5\""); `actual` is the measured length (5.75) the figure uses.
    var ins = data.productInseams || [];
    spec.inseams = (src.inseams || []).filter(function (o) {
      if (!ins.length) return true;
      return ins.some(function (v) { return num(v) === num(o.label); });
    }).map(function (o) {
      var match = null;
      ins.forEach(function (v) { if (!match && num(v) === num(o.label)) match = v; });
      return { label: o.label, actual: o.actual, value: match };
    });
    return spec;
  }

  /* ---------- instance ---------- */
  function Instance(data) {
    this.data = data;
    this.s = data.strings;
    this.spec = prepSpec(data);
    this.trigger = document.querySelector('[data-fit-finder-open="' + data.id + '"]');
    this.savedEl = document.querySelector('[data-fit-finder-saved="' + data.id + '"]');
    this.propEl = document.querySelector('[data-fit-finder-prop="' + data.id + '"]');
    this.scope = data.context === 'bundle' ? (this.trigger && this.trigger.closest('.bundle-builder')) : null;
    this.state = { step: 1, units: 'imperial', profile: {}, result: null, figLen: null, applied: false };
  }

  Instance.prototype.recommend = function (profile) {
    if (!E || !profile) return null;
    var r = E.recommend(this.spec, profile);
    return r && r.ok ? r : null;
  };

  // Product sells more than one inseam (the only case the inseam is part of the size).
  Instance.prototype.multiInseam = function () { return (this.data.productInseams || []).length > 1; };

  Instance.prototype.inseamValue = function (r) {
    if (!r || !r.inseam || !r.inseam.recommended || !this.multiInseam()) return null;
    var o = this.spec.inseams.filter(function (x) { return x.label === r.inseam.recommended; })[0];
    return o ? { label: o.label, value: o.value } : null;
  };

  // Tops ask chest + stomach; bottoms and briefs ask thighs.
  Instance.prototype.isTop = function () { return this.spec.type === 'top'; };
  Instance.prototype.hasShapes = function (p) {
    return !!p && (this.isTop() ? !!(p.chestShape && p.stomachShape) : !!p.thighShape);
  };

  Instance.prototype.updateSaved = function () {
    var p = loadProfile(), r = this.hasShapes(p) ? this.recommend(p) : null;
    if (this.savedEl) {
      var ins = this.inseamValue(r);
      this.savedEl.textContent = r ? (ins ? fill(this.s.saved_line_inseam, { size: r.size, inseam: ins.label }) : fill(this.s.saved_line, { size: r.size })) : '';
    }
    this.syncProp();
  };

  /* --- variant helpers (main context: theme.js radio picker) --- */
  Instance.prototype.radios = function (optName) {
    var formId = this.data.formId, out = [];
    each(document.querySelectorAll('input[type="radio"][data-variant-input]'), function (el) {
      if (el.name === optName && (!formId || el.getAttribute('form') === formId)) out.push(el);
    });
    return out;
  };
  Instance.prototype.checkedValue = function (optName) {
    var c = this.radios(optName).filter(function (el) { return el.checked; })[0];
    return c ? c.value : null;
  };
  // Variant the shopper would get with this size (and inseam), keeping their other choices (color).
  Instance.prototype.variantFor = function (size, inseamVal) {
    var d = this.data, self = this;
    var want = d.options.map(function (name) {
      if (name === d.sizeOption) return size;
      if (name === d.inseamOption && inseamVal) return inseamVal;
      return self.checkedValue(name);
    });
    return (d.variants || []).filter(function (v) {
      return v.o.every(function (val, i) { return want[i] == null || want[i] === val; });
    })[0] || null;
  };
  Instance.prototype.available = function (size, inseamVal) {
    if (this.data.context === 'bundle') return true;   // bundle-builder handles per-color stock
    var v = this.variantFor(size, inseamVal);
    return !v || v.a;
  };

  Instance.prototype.select = function (size, inseamVal) {
    if (this.data.context === 'bundle') {
      var sc = this.scope;
      if (!sc) return;
      var click = function (sel, val) {
        each(sc.querySelectorAll(sel), function (b) { if (b.dataset.value === val && !b.disabled && b.getAttribute('aria-pressed') !== 'true') b.click(); });
      };
      if (inseamVal) click('.bundle-builder__options--length [data-value]', inseamVal);
      click('.bundle-builder__options--waist [data-value]', size);
      return;
    }
    var pick = function (radios, val) {
      radios.forEach(function (el) { if (el.value === val && !el.checked) el.click(); });
    };
    if (inseamVal && this.data.inseamOption) pick(this.radios(this.data.inseamOption), inseamVal);
    pick(this.radios(this.data.sizeOption), size);
  };

  // Hidden line-item property _fit_finder = "L|high|w32" while the selected size matches. The usual waist
  // (a pants size) is logged so returns can be traced to the waists that run small; height and weight never are.
  Instance.prototype.propValue = function (size, inseamVal) {
    var p = loadProfile(), r = this.hasShapes(p) ? this.recommend(p) : null;
    if (!r || size !== r.size) return null;
    var ins = this.inseamValue(r);
    if (ins && inseamVal && inseamVal !== ins.value) return null;
    return r.size + '|' + r.confidence + '|w' + Math.round(p.waist);
  };
  Instance.prototype.syncProp = function () {
    if (!this.propEl) return;
    var v = this.propValue(this.checkedValue(this.data.sizeOption), this.data.inseamOption ? this.checkedValue(this.data.inseamOption) : null);
    this.propEl.value = v || '';
    this.propEl.disabled = !v;
  };

  Instance.prototype.params = function (r) {
    r = r || {};
    var ins = this.inseamValue(r);
    return { ff_style: this.spec.style_name, ff_size: r.size, ff_alternate: r.alternate || undefined,
      ff_confidence: r.confidence, ff_inseam: ins ? ins.label : undefined };
  };

  /* ---------- modal shell ---------- */
  function buildModal(s) {
    var wrap = document.createElement('div');
    wrap.className = 'ff-backdrop';
    wrap.hidden = true;
    wrap.innerHTML =
      '<div class="ff-modal" role="dialog" aria-modal="true" aria-labelledby="ffTitle">' +
        '<div class="ff-head"><h2 class="ff-title" id="ffTitle" tabindex="-1">' + esc(s.title) + '</h2>' +
        '<button type="button" class="ff-close" data-ff-close aria-label="' + esc(s.close) + '">' +
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>' +
        '<p class="ff-step" data-ff-step></p>' +
        '<p class="visually-hidden" aria-live="polite" data-ff-live></p>' +
        '<div data-ff-body></div>' +
      '</div>';
    document.body.appendChild(wrap);
    var m = { root: wrap, dialog: wrap.firstChild, title: wrap.querySelector('#ffTitle'), step: wrap.querySelector('[data-ff-step]'),
      live: wrap.querySelector('[data-ff-live]'), body: wrap.querySelector('[data-ff-body]'), lastFocus: null };
    wrap.addEventListener('click', function (e) {
      if (e.target === wrap || e.target.closest('[data-ff-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (wrap.hidden) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(m.dialog.querySelectorAll('button, select, input, [href], #ffTitle'), function (x) {
        return !x.disabled && x.offsetParent !== null && !(x.type === 'radio' && !x.checked);
      });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    return m;
  }

  function open(inst) {
    if (!modal) modal = buildModal(inst.s);
    active = inst;
    var st = inst.state, saved = loadProfile();
    st.applied = false; st.figLen = null;
    st.profile = saved ? JSON.parse(JSON.stringify(saved)) : (st.profile || {});
    // Saved shopper missing this product type's shape answers: skip step 1 and ask only those, once.
    st.oneMore = !!saved && !inst.hasShapes(saved);
    st.step = !saved ? 1 : st.oneMore ? 2 : 3;
    modal.lastFocus = document.activeElement;
    modal.root.hidden = false;
    document.documentElement.classList.add('ff-lock');
    render();
    modal.title.focus();
    track('fit_finder_open', { ff_style: inst.spec.style_name, ff_has_profile: !!saved });
    if (st.step === 3 && st.result) track('fit_finder_result', inst.params(st.result));
  }

  function close(applied) {
    if (!modal || modal.root.hidden) return;
    modal.root.hidden = true;
    document.documentElement.classList.remove('ff-lock');
    if (active && !applied && !active.state.applied) track('fit_finder_close', { ff_last_step: active.state.step });
    if (modal.lastFocus && modal.lastFocus.focus) modal.lastFocus.focus();
  }

  function announce(txt) { if (modal) { modal.live.textContent = ''; setTimeout(function () { modal.live.textContent = txt; }, 50); } }

  function goto(step) {
    active.state.step = step;
    if (step === 1) active.state.oneMore = false;
    render();
    modal.title.focus();
  }

  function render() {
    var inst = active, s = inst.s, st = inst.state;
    modal.body.innerHTML = st.step === 1 ? step1(inst) : st.step === 2 ? step2(inst) : step3(inst);
    // A returning shopper asked only the thigh question on a bottom gets no step line.
    var label = st.step === 2 && st.oneMore ? (inst.isTop() ? s.step_more_tops : '')
      : st.step < 3 ? fill(s.step, { step: st.step }) : s.result_step;
    modal.step.textContent = label;
    modal.step.hidden = !label;
    if (st.step < 3 && label) announce(label);
    else if (st.result) announce(s.result_step + ': ' + fill(s.size_big, { size: st.result.size }) + '. ' + fitLine(inst));
    wire(inst);
  }

  /* ---------- step 1: about you ---------- */
  function opt(v, t, cur) {
    return '<option value="' + esc(v) + '"' + (cur !== undefined && cur !== '' && cur !== null && String(cur) === String(v) ? ' selected' : '') + '>' + esc(t) + '</option>';
  }
  function step1(inst) {
    var s = inst.s, p = inst.state.profile, im = inst.state.units === 'imperial', h = '', i;
    var ft = p.heightIn ? Math.floor(Math.round(p.heightIn) / 12) : '', inch = p.heightIn ? Math.round(p.heightIn) % 12 : '';
    h += '<div class="ff-units" role="group" aria-label="' + esc(s.units) + '">' +
      '<button type="button" data-ff-units="imperial" aria-pressed="' + im + '">' + esc(s.units_imperial) + '</button>' +
      '<button type="button" data-ff-units="metric" aria-pressed="' + !im + '">' + esc(s.units_metric) + '</button></div>';
    if (im) {
      var fts = [4, 5, 6].map(function (x) { return opt(x, x + ' ' + s.ft_unit, ft); }).join('');
      var ins = ''; for (i = 0; i <= 11; i++) ins += opt(i, i + ' ' + s.in_unit, inch);
      h += '<fieldset class="ff-field" aria-describedby="ffErrHeight"><legend class="ff-label">' + esc(s.height) + '</legend><div class="ff-row">' +
        '<div><label class="ff-sublabel" for="ffFt">' + esc(s.feet) + '</label><select id="ffFt" aria-describedby="ffErrHeight">' + opt('', s.feet, '') + fts + '</select></div>' +
        '<div><label class="ff-sublabel" for="ffIn">' + esc(s.inches) + '</label><select id="ffIn" aria-describedby="ffErrHeight">' + opt('', s.inches, '') + ins + '</select></div>' +
        '</div><p class="ff-err" id="ffErrHeight"></p></fieldset>';
      h += '<div class="ff-field"><label class="ff-label" for="ffW">' + esc(s.weight_lb) + '</label>' +
        '<input id="ffW" type="number" inputmode="numeric" min="100" max="350" autocomplete="off" value="' + (p.weightLb ? Math.round(p.weightLb) : '') + '" aria-describedby="ffErrWeight"><p class="ff-err" id="ffErrWeight"></p></div>';
    } else {
      h += '<div class="ff-field"><label class="ff-label" for="ffCm">' + esc(s.height_cm) + '</label>' +
        '<input id="ffCm" type="number" inputmode="numeric" min="152" max="208" autocomplete="off" value="' + (p.heightIn ? Math.round(p.heightIn * 2.54) : '') + '" aria-describedby="ffErrHeight"><p class="ff-err" id="ffErrHeight"></p></div>';
      h += '<div class="ff-field"><label class="ff-label" for="ffKg">' + esc(s.weight_kg) + '</label>' +
        '<input id="ffKg" type="number" inputmode="numeric" min="45" max="160" autocomplete="off" value="' + (p.weightLb ? Math.round(p.weightLb / 2.2046) : '') + '" aria-describedby="ffErrWeight"><p class="ff-err" id="ffErrWeight"></p></div>';
    }
    var w = ''; for (i = 26; i <= 44; i++) w += opt(i, i, p.waist);
    h += '<div class="ff-field"><label class="ff-label" for="ffWaist">' + esc(s.waist) + '</label>' +
      '<select id="ffWaist" aria-describedby="ffHintWaist ffErrWaist">' + opt('', s.select, '') + w + '</select>' +
      '<p class="ff-hint" id="ffHintWaist">' + esc(s.waist_hint) + '</p><p class="ff-err" id="ffErrWaist"></p></div>';
    h += '<div class="ff-actions"><button type="button" class="btn ff-btn" data-ff-next1>' + esc(s.continue) + '</button></div>';
    return h;
  }

  var ERR_IDS = { height: ['ffFt', 'ffIn', 'ffCm'], weight: ['ffW', 'ffKg'], waist: ['ffWaist'] };
  var ERR_BOX = { height: 'ffErrHeight', weight: 'ffErrWeight', waist: 'ffErrWaist' };
  function setErr(field, msg) {
    var box = document.getElementById(ERR_BOX[field]);
    if (box) box.textContent = msg || '';
    ERR_IDS[field].forEach(function (id) { var n = document.getElementById(id); if (n) n.setAttribute('aria-invalid', msg ? 'true' : 'false'); });
  }
  function val(id) { var n = document.getElementById(id); return n ? n.value : ''; }
  function readStep1(inst, validate) {
    var p = inst.state.profile, im = inst.state.units === 'imperial', s = inst.s;
    if (im) {
      var ft = val('ffFt'), inch = val('ffIn');
      p.heightIn = ft !== '' && inch !== '' ? Number(ft) * 12 + Number(inch) : NaN;
      p.weightLb = Number(val('ffW')) || NaN;
    } else {
      p.heightIn = Number(val('ffCm')) / 2.54 || NaN;
      p.weightLb = Number(val('ffKg')) * 2.2046 || NaN;
    }
    p.waist = Number(val('ffWaist')) || NaN;
    if (!validate) return true;
    var n = E.normalizeProfile(p), msgs = { height: im ? s.err_height : s.err_height_cm, weight: im ? s.err_weight : s.err_weight_kg, waist: s.err_waist };
    ['height', 'weight', 'waist'].forEach(function (f) { setErr(f, ''); });
    n.errors.forEach(function (e) { if (msgs[e.field]) setErr(e.field, msgs[e.field]); });
    if (!n.ok) {
      var first = { height: im ? 'ffFt' : 'ffCm', weight: im ? 'ffW' : 'ffKg', waist: 'ffWaist' }[n.errors[0].field];
      var el = document.getElementById(first); if (el) el.focus();
    }
    return n.ok;
  }

  /* ---------- step 2: body shape ---------- */
  var SHAPES = {
    chest: [['slim', 'M22 10 L42 10 M22 10 L24 56 M42 10 L40 56 M24 56 L40 56'],
            ['average', 'M16 10 L48 10 M16 10 Q20 34 23 56 M48 10 Q44 34 41 56 M23 56 L41 56'],
            ['broad', 'M8 10 L56 10 M8 10 Q18 34 24 56 M56 10 Q46 34 40 56 M24 56 L40 56']],
    stomach: [['flat', 'M42 6 L42 58 M26 6 L26 58'],
              ['average', 'M42 6 L42 58 M26 6 Q18 32 26 58'],
              ['round', 'M42 6 L42 58 M26 6 Q4 34 26 58']]
  };
  function cards(s, name, legend, hint, cur) {
    var h = '<fieldset class="ff-field" aria-describedby="ffHint-' + name + '"><legend class="ff-label">' + esc(legend) + '</legend>' +
      '<p class="ff-hint ff-hint--top" id="ffHint-' + name + '">' + esc(hint) + '</p><div class="ff-cards">';
    SHAPES[name].forEach(function (sh) {
      var id = 'ffShape-' + name + '-' + sh[0];
      h += '<div class="ff-card"><input type="radio" name="ff_' + name + '" id="' + id + '" value="' + sh[0] + '"' + ((cur || 'average') === sh[0] ? ' checked' : '') + '>' +
        '<label for="' + id + '"><svg viewBox="0 0 64 64" aria-hidden="true" focusable="false"><path d="' + sh[1] + '"/></svg>' + esc(s[sh[0]]) + '</label></div>';
    });
    return h + '</div></fieldset>';
  }
  // Thighs are words only (no drawing); Athletic carries a helper line.
  function thighCards(s, cur) {
    var h = '<fieldset class="ff-field"><legend class="ff-label">' + esc(s.thighs_legend) + '</legend><div class="ff-cards">';
    ['slim', 'average', 'athletic'].forEach(function (v) {
      var id = 'ffShape-thigh-' + v, sub = v === 'athletic' ? '<span class="ff-card__sub">' + esc(s.athletic_hint) + '</span>' : '';
      h += '<div class="ff-card ff-card--text"><input type="radio" name="ff_thigh" id="' + id + '" value="' + v + '"' + ((cur || 'average') === v ? ' checked' : '') + '>' +
        '<label for="' + id + '">' + esc(s[v]) + sub + '</label></div>';
    });
    return h + '</div></fieldset>';
  }
  function step2(inst) {
    var s = inst.s, p = inst.state.profile;
    var questions = inst.isTop()
      ? cards(s, 'chest', s.chest_legend, s.chest_hint, p.chestShape) + cards(s, 'stomach', s.stomach_legend, s.stomach_hint, p.stomachShape)
      : thighCards(s, p.thighShape);
    // "One more step" has no step 1 to go back to, so it offers Edit my info instead of Back.
    var back = inst.state.oneMore
      ? '<button type="button" class="btn btn--secondary ff-btn" data-ff-edit>' + esc(s.edit) + '</button>'
      : '<button type="button" class="btn btn--secondary ff-btn" data-ff-back>' + esc(s.back) + '</button>';
    return questions + '<p class="ff-hint ff-hint--block">' + esc(s.shape_hint) + '</p>' +
      '<div class="ff-actions">' + back + '<button type="button" class="btn ff-btn" data-ff-next2>' + esc(s.see_size) + '</button></div>';
  }
  function readStep2(inst) {
    var q = function (n) { var el = modal.body.querySelector('input[name="ff_' + n + '"]:checked'); return el ? el.value : 'average'; };
    if (inst.isTop()) { inst.state.profile.chestShape = q('chest'); inst.state.profile.stomachShape = q('stomach'); }
    else inst.state.profile.thighShape = q('thigh');
  }

  /* ---------- result ---------- */
  function fitLine(inst) {
    var s = inst.s, spec = inst.spec, f = String(spec.fit || '').toLowerCase();
    if (spec.type === 'brief') return s.fit_brief;
    var line = s[(spec.type === 'top' ? 'fit_top_' : 'fit_bottom_') + f] || '';
    return (line ? line + ' ' : '') + (spec.type === 'top' ? s.fit_based_on : s.fit_based_on_bottom);
  }
  function step3(inst) {
    var s = inst.s, st = inst.state, r = inst.recommend(st.profile), h = '';
    st.result = r;
    if (!r) {
      return '<div class="ff-result"><p>' + esc(s.no_result) + '</p></div><div class="ff-actions"><button type="button" class="btn btn--secondary ff-btn" data-ff-edit>' + esc(s.edit) + '</button></div>';
    }
    var conf = { high: s.conf_high, medium: s.conf_medium, low: s.conf_low }[r.confidence] || s.conf_low;
    var ins = inst.inseamValue(r), insVal = ins ? ins.value : null;
    var inStock = inst.available(r.size, insVal);
    var altStock = r.alternate ? inst.available(r.alternate, insVal) : false;
    h += '<div class="ff-result"><p class="ff-pill ff-pill--' + esc(r.confidence) + '">' + esc(conf) + '</p>' +
      '<p class="ff-big">' + esc(fill(s.size_big, { size: r.size })) + '</p><p>' + esc(fitLine(inst)) + '</p>';
    if (r.alternate) {
      var closer = SIZE_ORDER.indexOf(r.alternate) < SIZE_ORDER.indexOf(r.size);
      h += '<p>' + esc(fill(closer ? s.alt_closer : s.alt_room, { size: r.alternate })) + '</p>';
    }
    if (r.notes && r.notes.length) h += '<ul class="ff-notes">' + r.notes.map(function (n) { return '<li>' + esc(n.text) + '</li>'; }).join('') + '</ul>';
    if (!inStock) h += '<p class="ff-stock">' + esc(fill(s.sold_out, { size: r.size })) + '</p>';
    h += '</div>';
    if (r.inseam && r.inseam.options && r.inseam.options.length) h += inseamBlock(inst, r);
    var sizeLabel = r.size + (ins ? ', ' + ins.label : '');
    var primary = inStock ? fill(s.apply, { size: sizeLabel }) : fill(s.notify, { size: r.size });
    h += '<div class="ff-actions"><button type="button" class="btn btn--secondary ff-btn" data-ff-edit>' + esc(s.edit) + '</button>' +
      '<button type="button" class="btn ff-btn" data-ff-apply="' + (inStock ? 'select' : 'notify') + '">' + esc(primary) + '</button></div>';
    if (!inStock && altStock) h += '<p class="ff-alt-action"><button type="button" class="ff-link" data-ff-alt>' + esc(fill(s.select_alt, { size: r.alternate })) + '</button></p>';
    h += '<p class="ff-clear"><button type="button" class="ff-link" data-ff-clear>' + esc(s.clear) + '</button></p>';
    return h;
  }

  /* ---------- inseam sketch (line drawing, legs only) ---------- */
  function inseamBlock(inst, r) {
    var s = inst.s, opts = r.inseam.options, st = inst.state;
    var pick = st.figLen && opts.some(function (o) { return o.label === st.figLen; }) ? st.figLen : (r.inseam.recommended || opts[0].label);
    st.figLen = pick;
    var cur = opts.filter(function (o) { return o.label === pick; })[0];
    var desc = function (o) { return o.description.label + (o.description.detail ? ', ' + o.description.detail : ''); };
    var h = '<h3 class="ff-h3">' + esc(s.length_heading) + '</h3><div class="ff-inseam"><div class="ff-figure">' + figureSVG(inst, r.body.heightIn, cur, opts, desc(cur)) + '</div>';
    h += '<div class="ff-lengths" role="group" aria-label="' + esc(s.length_group) + '">';
    opts.forEach(function (o) {
      h += '<button type="button" class="ff-chip" data-ff-len="' + esc(o.label) + '" aria-pressed="' + (o.label === pick) + '">' +
        '<span class="ff-chip__label">' + esc(o.label) + ' ' + esc(o.description.label) +
        (o.label === r.inseam.recommended ? ' <span class="ff-tag">' + esc(s.recommended) + '</span>' : '') + '</span>' +
        (o.description.detail ? '<span class="ff-chip__detail">' + esc(o.description.detail) + '</span>' : '') + '</button>';
    });
    h += '</div></div><p class="visually-hidden" aria-live="polite">' + esc(fill(s.length_showing, { length: cur.label, description: desc(cur) })) + '</p>';
    return h;
  }

  // Body outline traced from the Modern Fit sketch on bearbottomclothing.com/pages/pant-fit-guide
  // (2500 px canvas, centre x = 1250), left leg as [imageY, imageX]. Warped onto the engine's landmarks.
  var T_OUT = [[140,949],[250,949],[300,942],[340,934],[380,929],[420,924],[460,920],[500,916],[540,913],[580,910],[620,907],[660,906],[700,904],[740,902],[820,901],[900,902],[980,906],[1060,912],[1140,919],[1220,924],[1300,926],[1380,921],[1460,913],[1540,911],[1620,915],[1700,924],[1780,939],[1840,953],[1900,969],[1960,984],[2020,990],[2080,984],[2140,972],[2200,970],[2240,949],[2280,934],[2320,951],[2345,1000],[2365,1060]];
  var T_IN = [[650,1247],[660,1239],[700,1233],[740,1229],[820,1221],[900,1212],[980,1202],[1060,1191],[1140,1179],[1220,1165],[1300,1148],[1340,1137],[1380,1120],[1420,1112],[1460,1113],[1540,1118],[1620,1121],[1700,1118],[1780,1107],[1840,1097],[1900,1094],[1960,1101],[2020,1113],[2080,1126],[2140,1130],[2200,1130],[2260,1129],[2300,1126],[2340,1120],[2365,1099]];

  function figureSVG(inst, heightIn, cur, opts, curDesc) {
    // Hems are placed in inches, so hem vs knee is true to scale at every height.
    var subtype = inst.spec.subtype;
    var geo = E.legGeometry(heightIn), k = 500 / heightIn, Y = function (v) { return 500 - v * k; }, f = function (n) { return n.toFixed(1); };
    var yW = Y(heightIn * 0.575), yC = Y(geo.crotch), yK = Y(geo.kneeTop), yAnk = Y(heightIn * 0.06), crop = yW - 30;
    var LY = [140, 250, 650, 1330, 1900, 2372], OY = [crop, yW, yC, yK, yAnk, 500];
    var mapY = function (y) { for (var i = 0; i < LY.length - 1; i++) { if (y <= LY[i + 1]) return OY[i] + (OY[i + 1] - OY[i]) * (y - LY[i]) / (LY[i + 1] - LY[i]); } return 500; };
    var mapX = function (x) { return 100 + (x - 1250) * 0.133; };
    var O = T_OUT.map(function (p) { return [mapY(p[0]), mapX(p[1])]; }), N = T_IN.map(function (p) { return [mapY(p[0]), mapX(p[1])]; });
    var o = O.filter(function (p) { return p[0] <= yAnk + 1; }), n = N.filter(function (p) { return p[0] <= yAnk + 1; });
    function xAt(pts, y) { if (y <= pts[0][0]) return pts[0][1]; for (var i = 0; i < pts.length - 1; i++) { var a = pts[i], b = pts[i + 1]; if (y >= a[0] && y <= b[0]) return a[1] + (b[1] - a[1]) * (y - a[0]) / (b[0] - a[0]); } return pts[pts.length - 1][1]; }
    function cont(p) {
      var d = '';
      for (var i = 0; i < p.length - 1; i++) {
        var p0 = p[i - 1] || p[i], p1 = p[i], p2 = p[i + 1], p3 = p[i + 2] || p2;
        d += ' C' + f(p1[0] + (p2[0] - p0[0]) / 6) + ' ' + f(p1[1] + (p2[1] - p0[1]) / 6) + ' ' + f(p2[0] - (p3[0] - p1[0]) / 6) + ' ' + f(p2[1] - (p3[1] - p1[1]) / 6) + ' ' + f(p2[0]) + ' ' + f(p2[1]);
      }
      return d;
    }
    var R = function (x) { return 200 - x; };
    var Lo = O.slice(1).map(function (p) { return [p[1], p[0]]; }), Li = N.map(function (p) { return [p[1], p[0]]; });
    var Ro = Lo.map(function (p) { return [R(p[0]), p[1]]; }), Ri = Li.map(function (p) { return [R(p[0]), p[1]]; });
    var outline = 'M' + f(Lo[0][0]) + ' ' + f(crop) + ' L' + f(Lo[0][0]) + ' ' + f(Lo[0][1]) + cont(Lo) + ' L' + f(Li[Li.length - 1][0]) + ' ' + f(Li[Li.length - 1][1]) + cont(Li.slice().reverse()) +
      ' Q100 ' + f(yC - 3) + ' ' + f(Ri[0][0]) + ' ' + f(Ri[0][1]) + cont(Ri) + ' L' + f(Ro[Ro.length - 1][0]) + ' ' + f(Ro[Ro.length - 1][1]) + cont(Ro.slice().reverse()) + ' L' + f(Ro[0][0]) + ' ' + f(crop);
    function garment(opt, cls, details) {
      var jog = subtype === 'jogger' && opt.actual > 12, isShort = opt.actual < 12;
      var yh = Math.min(Y(opt.hem.hemHeight), 490), top = yW - 6, eo = isShort ? 3.5 : 4.5, ei = isShort ? 2.5 : 3;
      var OL = [], IL = [], y, mo = xAt(o, yh) - eo, mi = xAt(n, Math.max(yh, yC)) + ei;
      if (jog) { mo += 1.5; mi -= 1; }
      // fabric hangs from the widest point below it, so it never cuts into the body
      OL.push([mo, yh]); for (y = yh - 6; y > top; y -= 6) { mo = Math.min(mo, xAt(o, y) - eo); OL.push([mo, y]); } OL.push([Math.min(mo, xAt(o, top) - 2), top]); OL.reverse();
      IL.push([mi, yh]); for (y = yh - 6; y > yC + 3; y -= 6) { mi = Math.max(mi, xAt(n, y) + ei); IL.push([mi, y]); } IL.push([100, yC + 2]); IL.reverse();
      var pl = function (a) { return a.map(function (p) { return ' L' + f(p[0]) + ' ' + f(p[1]); }).join(''); };
      var mir = function (a) { return a.map(function (p) { return [R(p[0]), p[1]]; }); };
      var d = 'M' + f(R(OL[0][0])) + ' ' + f(top) + pl(mir(OL)) + pl(mir(IL).reverse()) + pl(IL) + pl(OL.slice().reverse()) + ' Z';
      var out = '<path class="ff-' + cls + '" d="' + d + '"/>';
      if (!details) return out;
      var wb = top + 8, lx = OL[0][0] + 1, hemO = OL[OL.length - 1][0], hemI = IL[IL.length - 1][0];
      var det = 'M' + f(lx) + ' ' + f(wb) + ' L' + f(R(lx)) + ' ' + f(wb) + ' M100 ' + f(wb) + ' L100 ' + f(yC - 8) + ' M67 ' + f(wb) + ' L62 ' + f(wb + 26) + ' M133 ' + f(wb) + ' L138 ' + f(wb + 26);
      var dash = 'M104 ' + f(wb + 1) + ' L104 ' + f(yC - 18) + ' Q104 ' + f(yC - 12) + ' 100 ' + f(yC - 11);
      if (jog) {
        det += ' M' + f(hemO) + ' ' + f(yh - 9) + ' L' + f(hemI) + ' ' + f(yh - 9) + ' M' + f(R(hemO)) + ' ' + f(yh - 9) + ' L' + f(R(hemI)) + ' ' + f(yh - 9);
        for (var cx = hemO + 2; cx < hemI - 1; cx += 2.5) det += ' M' + f(cx) + ' ' + f(yh - 8) + ' L' + f(cx) + ' ' + f(yh - 1) + ' M' + f(R(cx)) + ' ' + f(yh - 8) + ' L' + f(R(cx)) + ' ' + f(yh - 1);
      } else {
        dash += ' M' + f(hemO + 1) + ' ' + f(yh - 4) + ' L' + f(hemI - 1) + ' ' + f(yh - 4) + ' M' + f(R(hemO + 1)) + ' ' + f(yh - 4) + ' L' + f(R(hemI - 1)) + ' ' + f(yh - 4);
      }
      return out + '<path class="ff-gdet" d="' + det + '"/><path class="ff-gdet-d" d="' + dash + '"/><circle class="ff-gdet" cx="100" cy="' + f(top + 4) + '" r="2"/>';
    }
    var g = '';
    opts.forEach(function (x) { if (x !== cur) g += garment(x, 'ghost', false); });
    g += garment(cur, 'garment', true);
    var ft = Math.floor(Math.round(heightIn) / 12), inch = Math.round(heightIn) % 12, vt = crop - 4;
    var label = fill(inst.s.figure_label, { feet: ft, inches: inch, length: cur.label, description: curDesc });
    return '<svg viewBox="-8 ' + f(vt) + ' 216 ' + f(514 - vt) + '" role="img" aria-label="' + esc(label) + '">' +
      '<path class="ff-body" d="' + outline + '"/>' + g + '<text x="100" y="512" text-anchor="middle" aria-hidden="true">' + ft + '′' + inch + '″</text></svg>';
  }

  /* ---------- wiring ---------- */
  function on(sel, fn) { var el = modal.body.querySelector(sel); if (el) el.addEventListener('click', fn); }
  function wire(inst) {
    var st = inst.state;
    each(modal.body.querySelectorAll('[data-ff-units]'), function (b) {
      b.addEventListener('click', function () {
        readStep1(inst, false); st.units = b.getAttribute('data-ff-units'); render();
        var nb = modal.body.querySelector('[data-ff-units="' + st.units + '"]'); if (nb) nb.focus();
      });
    });
    on('[data-ff-next1]', function () { if (readStep1(inst, true)) { track('fit_finder_step', { ff_step: 1 }); goto(2); } });
    on('[data-ff-back]', function () { readStep2(inst); goto(1); });
    on('[data-ff-next2]', function () {
      readStep2(inst); saveProfile(st.profile); track('fit_finder_step', { ff_step: 2 });
      goto(3);
      if (st.result) track('fit_finder_result', inst.params(st.result));
      inst.updateSaved();
    });
    on('[data-ff-edit]', function () { goto(1); });
    on('[data-ff-clear]', function () {
      clearProfile(); st.profile = {}; st.result = null; inst.updateSaved();
      goto(1); announce(inst.s.cleared);
    });
    on('[data-ff-apply]', function (e) {
      var r = st.result, ins = inst.inseamValue(r), notify = e.currentTarget.getAttribute('data-ff-apply') === 'notify';
      st.applied = true;
      inst.select(r.size, ins ? ins.value : null);
      track('fit_finder_apply', inst.params(r));
      close(true);
      inst.updateSaved();
      if (notify) setTimeout(openBackInStock, 350);
    });
    on('[data-ff-alt]', function () {
      var r = st.result, ins = inst.inseamValue(r);
      st.applied = true;
      inst.select(r.alternate, ins ? ins.value : null);
      track('fit_finder_apply', inst.params({ size: r.alternate, alternate: r.size, confidence: r.confidence, inseam: r.inseam }));
      close(true);
      inst.updateSaved();
    });
    each(modal.body.querySelectorAll('[data-ff-len]'), function (b) {
      b.addEventListener('click', function () {
        var v = b.getAttribute('data-ff-len'); st.figLen = v; render();
        each(modal.body.querySelectorAll('[data-ff-len]'), function (x) { if (x.getAttribute('data-ff-len') === v) x.focus(); });
      });
    });
  }

  // Klaviyo back-in-stock replaces Add to Cart with its own trigger once a sold-out variant is selected.
  function openBackInStock() {
    var t = document.querySelector('.klaviyo-bis-trigger');
    if (t) { t.click(); }
  }

  /* ---------- boot ---------- */
  function boot() {
    E = window.BBFitEngine;
    if (!E) return;
    each(document.querySelectorAll('script[data-fit-finder]'), function (el) {
      var id = el.getAttribute('data-fit-finder');
      if (instances[id]) return;
      try { instances[id] = new Instance(JSON.parse(el.textContent)); } catch (e) { return; }
      instances[id].updateSaved();
    });
    // Keep the _fit_finder property in step with whatever size the shopper picks.
    document.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('input[data-variant-input]')) return;
      for (var id in instances) instances[id].syncProp();
    });
  }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-fit-finder-open]');
    if (!t) return;
    var inst = instances[t.getAttribute('data-fit-finder-open')];
    if (inst) { e.preventDefault(); e.stopPropagation(); open(inst); }
  });

  window.BBFitFinder = {
    // bundle-builder.js: properties to add to each item when the chosen size matches.
    bundleProps: function (container, size, length) {
      for (var id in instances) {
        var inst = instances[id];
        if (inst.data.context === 'bundle' && inst.scope && inst.scope === container) {
          var v = inst.propValue(size, length);
          return v ? { _fit_finder: v } : null;
        }
      }
      return null;
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
